"use client"

import styled from "@emotion/styled"
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import CustomBreadcrumbs from "../shared/CustomBreadcrumbs"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const BreadcrumbsWrapper = styled.div`
  max-width: 1070px;
  width: 100%;
  margin: auto;
  padding: 0 16px;
`

const ContentBox = styled.div`
  display: flex;
  max-width: 1000px;
  width: 100%;
  padding: 48px 16px;
  flex-direction: column;
  gap: 24px;
`

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledTable = styled(Table)`
  margin-top: 16px;
  border: 1px solid ${(props) => props.theme.palette.divider};

  th,
  td {
    border: 1px solid ${(props) => props.theme.palette.divider};
    text-align: center;
    padding: 8px;
  }

  th {
    background-color: ${(props) => props.theme.palette.grey[100]};
    font-weight: bold;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 16px;
`

const ContactInfo = styled(Box)`
  background-color: ${(props) => props.theme.palette.grey[100]};
  padding: 24px;
  border-radius: 12px;
  margin-top: 16px;
`

const IndentedList = styled.ul`
  margin: 16px 0;
  padding-right: 40px;
  list-style-type: disc;

  li {
    margin-bottom: 12px;
    line-height: 1.6;
  }
`

const StyledMainHeading = styled(Typography)<{ component?: React.ElementType }>`
  font-weight: bold;
`

const StyledSectionHeading = styled(Typography)<{ component?: React.ElementType }>`
  text-decoration: underline;
  font-weight: bold;
`

const StyledSubHeading = styled(Typography)<{ component?: React.ElementType }>`
  text-decoration: underline;
  font-weight: bold;
  margin-top: 16px;
  font-size: ${(props) => props.theme.typography.h5.fontSize};
`

interface LocationAccessibility {
  name: string
  address: string
  elevators: boolean
  hearingAids: boolean
  accessibleDoor: boolean
  accessiblePath: boolean
  disabledParking: boolean
  accessibilityStatus: boolean
  signage: boolean
  serviceDesk: boolean
}

const locationsData: LocationAccessibility[] = [
  {
    name: "משרדי החברה",
    address: "א.ת ניר צבי",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם פורד תל אביב",
    address: "רח' החרש 20, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם מאזדה/דונגפנג/פורד חיפה",
    address: "שד' ההסתדרות 50, חיפה",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם מאזדה/דונגפנג/פורד ירושלים",
    address: "רח' פייר קניג 40, ירושלים",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם מאזדה/דונגפנג/פורד רעננה",
    address: "רח' תדהר 2, רעננה",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם מאזדה/דונגפנג/NIO תל אביב",
    address: "רח' החרש 20, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם דונגפנג ראשל\"צ",
    address: "רח' הכשרת הישוב 23, ראשל\"צ",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם דונגפנג הרצליה",
    address: "רח' הסדנאות 1, הרצליה",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם BMW/MINI חיפה",
    address: "רח' בר יהודה 69, נשר",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם BMW/MINI ירושלים",
    address: "רח' פייר קניג 40, ירושלים",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם BMW/MINI תל אביב",
    address: "רח' החרש 20, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם NIO הרצליה",
    address: "רח' אריה שנקר 7, הרצליה פיתוח",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם BMW Motorrad תל אביב",
    address: "רח' המסגר 16, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "אולם BPS וטרייד אין BMW, תל אביב",
    address: "רח' המסגר 16, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "מוסך מרכזי",
    address: "א.ת ניר צבי",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
  {
    name: "מוסך מרכזי BMW Motorrad",
    address: "רח' חומה ומגדל 3, תל אביב",
    elevators: true,
    hearingAids: true,
    accessibleDoor: true,
    accessiblePath: true,
    disabledParking: true,
    accessibilityStatus: true,
    signage: true,
    serviceDesk: true,
  },
]

export default function Accessibility(): React.ReactElement {
  return (
    <StyledLayout>
      <BreadcrumbsWrapper>
        <CustomBreadcrumbs
          current={{ title: "הצהרת נגישות", url: "/accessibility" }}
          parents={[{ title: "ראשי", url: "/" }]}
        />
      </BreadcrumbsWrapper>
      <ContentBox>
        <StyledMainHeading variant="h2" component="h1">
          הצהרת נגישות חברת דלק מוטורס בע&quot;מ
        </StyledMainHeading>

        <Section>
          <Typography variant="body1">
            דלק מוטורס בע&quot;מ משקיעה את מירב המאמצים ומשאבים רבים על מנת לספק לכל לקוחותיו שירות
            שוויוני, מכובד, נגיש ומקצועי. בהתאם לחוק שוויון זכויות לאנשים עם מוגבלויות
            תשנ&quot;ח-1998 ולתקנות שהותקנו מכוחו, מושקעים מאמצים ומשאבים רבים בביצוע התאמות הנגישות
            הנדרשות שיביאו לכך שאדם בעל מוגבלות יוכל לקבל את השירותים הניתנים לכלל הלקוחות, באופן
            עצמאי ושוויוני.
          </Typography>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            הסדרי נגישות מבנים
          </StyledSectionHeading>
          <Typography variant="body1">
            דלק מוטורס בע&quot;מ מקדמת את נגישות המבנים שבבעלותה או בחזקתה לטובת אנשים בעלי
            מוגבלויות, כדי להקל על בעלי מוגבלויות בגישה ובקבלת שירות.
          </Typography>

          <StyledSubHeading variant="h6" component="h3">
            להלן הסדרי הנגישות הקיימים במבנים בבעלות דלק מוטורס:
          </StyledSubHeading>

          <TableWrapper>
            <StyledTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>שם</TableCell>
                  <TableCell>כתובת</TableCell>
                  <TableCell>מצב נגישות</TableCell>
                  <TableCell>חניות נכים</TableCell>
                  <TableCell>דרכי גישה</TableCell>
                  <TableCell>דלת נגישה</TableCell>
                  <TableCell>עזרים ללקויי שמיעה</TableCell>
                  <TableCell>מעלית</TableCell>
                  <TableCell>עמדת שירות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locationsData.map((location, index) => (
                  <TableRow key={index}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.address}</TableCell>
                    <TableCell>{location.accessibilityStatus ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.disabledParking ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.accessiblePath ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.accessibleDoor ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.hearingAids ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.elevators ? "נגיש" : "לא נגיש"}</TableCell>
                    <TableCell>{location.serviceDesk ? "נגיש" : "לא נגיש"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableWrapper>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            מרכזי שירות
          </StyledSectionHeading>
          <Typography variant="body1">
            מרכזי השירות הם גופים עצמאיים האחראים לעמידה בדרישות הנגישות של מתקניהם ואתריהם. על לקוח
            עם מוגבלות המבקש לקבל שירות במרכז שירות מסוים, ליצור קשר עם מרכז השירות לקבלת מידע בנושא
            הנגישות.
          </Typography>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            שירות לקוחות נגיש
          </StyledSectionHeading>
          <Typography variant="body1">
            <strong>הדרכות עובדים לשירות נגיש</strong> - מתקיימות הדרכות להכרת תחום הנגישות, יצירת
            מודעות בקרב העובדים והקניית כלים מעשיים למתן שירות נגיש.
          </Typography>
          <Typography variant="body1">
            <strong>מוקד טלפוני נגיש</strong> - המענה הקולי הונגש כך שהמידע מועבר בשפה ברורה וללא
            מוסיקת רקע.
          </Typography>
          <Typography variant="body1">
            <strong>אמצעים נוספים ליצירת קשר:</strong>
          </Typography>
          <Typography variant="body1">
            דואר אלקטרוני: <a href="mailto:s.uzon@delekmotors.co.il">s.uzon@delekmotors.co.il</a>
          </Typography>
          <Typography variant="body1">
            פניה באמצעות הדואר: אזור תעשייה מושב ניר צבי, ת.ד. 200, 7290500
          </Typography>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            נגישות אתר האינטרנט
          </StyledSectionHeading>
          <Typography variant="body1">
            אתר אינטרנט נגיש הוא אתר המאפשר לאנשים עם מוגבלות ולאנשים מבוגרים לגלוש באותה רמה של
            יעילות והנאה ככל הגולשים. כ-20 עד 25 אחוזים מהאוכלוסייה נתקלים בקשיי שימוש באינטרנט
            ועשויים להיטיב מתכני אינטרנט נגישים יותר, כך על פי מחקר שנערך בשנת 2003 ע&quot;י חברת
            מייקרוסופט.
          </Typography>
          <Typography variant="body1">
            חברת דלק מוטורס בע&quot;מ מאמינה ופועלת למען שוויון הזדמנויות במרחב האינטרנטי לבעלי
            לקויות מגוונות ואנשים הנעזרים בטכנולוגיה מסייעת לשימוש במחשב.
          </Typography>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            מידע על נגישות האתר
          </StyledSectionHeading>
          <IndentedList>
            <li>
              <Typography variant="body1" component="span">
                אתר זה עומד בדרישות תקנות שיוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות)
                התשע&quot;ג 2013.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                התאמות הנגישות בוצעו עפ&quot;י המלצות התקן הישראלי (ת&quot;י 5568) לנגישות תכנים
                באינטרנט ברמת AA ומסמך WCAG2.0 הבינלאומי.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                הבדיקות נבחנו לתאימות הגבוהה ביותר עבור דפדפן כרום.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                האתר מספק מבנה סמנטי עבור טכנולוגיות מסייעות ותמיכה בדפוס השימוש המקובל להפעלה עם
                מקלדת בעזרת מקשי החיצים, Enter ו-Esc ליציאה מתפריטים וחלונות.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                מותאם לתצוגה בדפדפנים הנפוצים ולשימוש בטלפון הסלולרי.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                לשם קבלת חווית גלישה מיטבית עם תוכנת הקראת מסך, אנו ממליצים לשימוש בתוכנת NVDA
                העדכנית ביותר.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                מסמכים או סרטוני וידאו שעלו לאתר לפני אוקטובר 2017 ייתכן שלא נגישים באופן מלא. במידה
                שנתקלתם במסמך כזה או בסרטון, תוכלו לפנות לרכזת נגישות בחברה ואנחנו נדאג להנגיש לכם
                את המידע.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                <strong>מסירת מידע בפורמט נגיש</strong> - החברה מעמידה עבור לקוחותיה אפשרות לקבלת
                מידע בפורמטים נגישים. מסירת המידע הינה ללא עלות ומיועדת עבור אנשים עם מוגבלות.
                לפניות ומידע בנושא נגישות ניתן ליצור קשר עם רכז הנגישות של החברה שפרטיו מופיעים
                בהמשך ההצהרה.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" component="span">
                בדיקות ויעוץ הנגישות בוצעו על ידי חברת A2Z נגישות ושיווק באינטרנט{" "}
                <a href="https://a-2-z.co.il" target="_blank" rel="noopener noreferrer">
                  https://a-2-z.co.il
                </a>
              </Typography>
            </li>
          </IndentedList>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            פרטי קשר רכז הנגישות בחברה
          </StyledSectionHeading>
          <Typography variant="body1">
            דלק מוטורס בע&quot;מ תשמח לקבל פניות מידע ו/או הצעות שיפור על מנת לשפר את השירות ללקוח.
            זאת, בימים א-ה בין השעות 08:00 עד 17:00 את הפעילות בנושאים חשובים אלו בקבוצה מרכז רכז
            הנגישות שלנו, שפרטיו:
          </Typography>
          <ContactInfo>
            <Typography variant="body1">
              <strong>שם:</strong> שימי עוזון
            </Typography>
            <Typography variant="body1">
              <strong>טלפון:</strong> <a href="tel:08-9139976">08-9139976</a>
            </Typography>
            <Typography variant="body1">
              <strong>פקס:</strong> 08-9139979
            </Typography>
            <Typography variant="body1">
              <strong>דוא&quot;ל:</strong>{" "}
              <a href="mailto:s.uzon@delekmotors.co.il">s.uzon@delekmotors.co.il</a>
            </Typography>
          </ContactInfo>
        </Section>

        <Section>
          <StyledSectionHeading variant="h5" component="h2">
            פרסום הצהרת הנגישות
          </StyledSectionHeading>
          <Typography variant="body2" color="textSecondary">
            הצהרת הנגישות עודכנה ביום: 09.12.2025
          </Typography>
        </Section>
      </ContentBox>
    </StyledLayout>
  )
}
